import React from "react";
import type {
  ClusteredTableRow,
  RawTableRow,
} from "@/lib/utils/marketSimilarity/types";

interface MarketStatuses {
    ts2vec: {
        status: boolean;
        missingDates: string[];
        chosen_date: string;
        original_date: string;
        usedAdjacentDate: boolean;
    }
}

// Helper function to format rally amount values
const formatRallyAmount = (value: number | null): string => {
    if (value === null) return '-';
  return value.toFixed(2);
};

type Props = {
  statuses: MarketStatuses;
  rows: RawTableRow[] | ClusteredTableRow[];
  loading: boolean;
  showEuclideanRank: boolean;
  showCosineRank: boolean;
  showFeatureData: boolean;
  showAverageLighthouseCorrelScore: boolean;
};

function isClusteredRows(
  rows: RawTableRow[] | ClusteredTableRow[]
): rows is ClusteredTableRow[] {
  return (
    rows.length > 0 &&
    (rows[0] as ClusteredTableRow).startDate !== undefined &&
    (rows[0] as ClusteredTableRow).endDate !== undefined
  );
}

export function ResultsTable({
  statuses,
  rows,
  loading,
  showEuclideanRank,
  showCosineRank,
  showFeatureData,
  showAverageLighthouseCorrelScore,
}: Props) {
  const clustered = isClusteredRows(rows);
  const baseColumns = clustered ? 11 : 9; // clustered uses startDate + endDate in addition to single date, both use rally columns
  const colSpan =
    baseColumns +
    (showEuclideanRank ? 1 : 0) +
    (showCosineRank ? 1 : 0) +
    (showFeatureData ? 8 : 0) +
    (showAverageLighthouseCorrelScore ? 1 : 0);

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="min-w-full border border-gray-300 text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-3 py-2 border-b text-left">date</th>
            {clustered && (
              <>
                <th className="px-3 py-2 border-b text-left">startDate</th>
                <th className="px-3 py-2 border-b text-left">endDate</th>
              </>
            )}
            <th className="px-3 py-2 border-b text-left">euclideanDistance</th>
            {showFeatureData && (
              <th className="px-3 py-2 border-b text-left">cosineDistance</th>
            )}
            {showEuclideanRank && (
              <th className="px-3 py-2 border-b text-left">euclideanRank</th>
            )}
            {showCosineRank && (
              <th className="px-3 py-2 border-b text-left">cosineRank</th>
            )}
            {showAverageLighthouseCorrelScore && (
              <th className="px-3 py-2 border-b text-left">avgCorrel</th>
            )}
            {showFeatureData && (
              <th className="px-3 py-2 border-b text-left">Ts2Vec</th>
            )}
            {showFeatureData && (
              <th className="px-3 py-2 border-b text-left">close</th>
            )}
            {showFeatureData && (
              <th className="px-3 py-2 border-b text-left">
                contAdjustedClose
              </th>
            )}
            {showFeatureData && (
              <th className="px-3 py-2 border-b text-left">stxUseScore</th>
            )}
            {showFeatureData && (
              <th className="px-3 py-2 border-b text-left">seasonalScoreV1</th>
            )}
            {showFeatureData && (
              <th className="px-3 py-2 border-b text-left">seasonalScoreV2</th>
            )}
            {showFeatureData && (
              <th className="px-3 py-2 border-b text-left">mmScore</th>
            )}
            {showFeatureData && (
              <th className="px-3 py-2 border-b text-left">algoScoreV3</th>
            )}
            {showFeatureData && (
              <th className="px-3 py-2 border-b text-left">rsi</th>
            )}
            <th className="px-3 py-2 border-b text-left">rally10d</th>
            <th className="px-3 py-2 border-b text-left">rally15d</th>
            <th className="px-3 py-2 border-b text-left">rally20d</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td className="px-3 py-6 border-t text-center" colSpan={colSpan}>
                <div className="flex items-center justify-center gap-3 text-gray-600">
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <span>Loading data…</span>
                </div>
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td className="px-3 py-3 border-t text-center" colSpan={colSpan}>
                No data yet. Adjust inputs and click &quot;Fetch Similarity
                Data&quot;.
              </td>
            </tr>
          ) : (
            rows.map((row, idx) => {
              const key = `${(row as ClusteredTableRow).startDate}-${
                (row as ClusteredTableRow).endDate
              }-${idx}`;
              return (
                <tr
                  key={key}
                  className={idx % 2 === 0 ? "bg-white" : "bg-gray-200"}
                >
                  <td className="px-3 py-2 border-t">
                    {(row as RawTableRow).date}
                  </td>
                  {clustered && (
                    <>
                      <td className="px-3 py-2 border-t">
                        {(row as ClusteredTableRow).startDate}
                      </td>
                      <td className="px-3 py-2 border-t">
                        {(row as ClusteredTableRow).endDate}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 border-t text-left">
                    {row.normalizedEuclideanDistance.toFixed(3)}
                  </td>
                  {showFeatureData && (
                    <td className="px-3 py-2 border-t text-left">
                      {row.normalizedCosineDistance.toFixed(3)}
                    </td>
                  )}
                  {showEuclideanRank && (
                    <td className="px-3 py-2 border-t text-left">
                      {row.euclideanRank}
                    </td>
                  )}
                  {showCosineRank && (
                    <td className="px-3 py-2 border-t text-left">
                      {row.cosineRank}
                    </td>
                  )}
                  {showAverageLighthouseCorrelScore && (
                    <td className="px-3 py-2 border-t text-left">
                      {row.meanLighthouseCorrelScore}
                    </td>
                  )}
                  {showFeatureData && (
                    <td className="px-3 py-2 border-t text-left">{statuses.ts2vec?.status ? row.ts2vec : "--"}</td>
                  )}
                  {showFeatureData && (
                    <td className="px-3 py-2 border-t text-left">{row.close}</td>
                  )}
                  {showFeatureData && (
                    <td className="px-3 py-2 border-t text-left">
                      {row.contAdjustedClose}
                    </td>
                  )}
                  {showFeatureData && (
                    <td className="px-3 py-2 border-t text-left">
                      {row.stxUseScore}
                    </td>
                  )}
                  {showFeatureData && (
                    <td className="px-3 py-2 border-t text-left">
                      {row.seasonalScoreV1}
                    </td>
                  )}
                  {showFeatureData && (
                    <td className="px-3 py-2 border-t text-left">
                      {row.seasonalScoreV2}
                    </td>
                  )}
                  {showFeatureData && (
                    <td className="px-3 py-2 border-t text-left">
                      {row.mmScore}
                    </td>
                  )}
                  {showFeatureData && (
                    <td className="px-3 py-2 border-t text-left">
                      {row.algoScoreV3}
                    </td>
                  )}
                  {showFeatureData && (
                    <td className="px-3 py-2 border-t text-left">{row.rsi}</td>
                  )}
                  <td className="px-3 py-2 border-t text-left">
                    {formatRallyAmount(row.rallyAmount10d)}
                  </td>
                  <td className="px-3 py-2 border-t text-left">
                    {formatRallyAmount(row.rallyAmount15d)}
                  </td>
                  <td className="px-3 py-2 border-t text-left">
                    {formatRallyAmount(row.rallyAmount20d)}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
